import {
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import SubjectSection from "../../components/SubjectSection";
import DesignSection from "../../components/DesignSection";
import DesignEmail from "../../components/DesignEmail";
import { useContext, useEffect, useState } from "react";
import { CampaignContext } from "../../Context";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { cloneDeep } from "lodash";
import { isTruthy } from "helpers/methods";
import { SubmitCampaign } from "types/Campaign";
import ScheduleSection from "../../components/ScheduleSection";
import TestModal from "../../components/TestModal";
import AttachmentSection from "../../components/AttachmentSection";

interface CustomProps {
  setActiveStep: Function;
  setChildCampaigns: Function;
  childCampaigns: SubmitCampaign[];
}

const Step2 = (props: CustomProps) => {
  const [isDesignEmailModalOpen, setIsDesignEmailModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const { submitCampaign, setSubmitCampaign, managingStep } =
    useContext(CampaignContext);
  const [editName, setEditName] = useState(false);
  const [formFields, setFormFields] = useState({
    name: {
      value: "",
      error: "",
    },
  });
  const [design, setDesign] = useState({
    content: submitCampaign.campaign.content ?? "",
    contentPlain: "",
    contentJson: submitCampaign.campaign.contentJson ?? {},
  });
  const [isTimeInPast, setIsTimeInPast] = useState(false);

  const saveCampaignName = () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.name.value)) {
      error = true;
      clonedFormFields.name.error = "Please enter the campaign name";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    setSubmitCampaign({
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        name: formFields.name.value.trim(),
      },
    });
    setEditName(false);
  };

  const disableSendTestMail = () => {
    return (
      !isTruthy(submitCampaign.campaign.subject) ||
      !isTruthy(submitCampaign.campaign.content)
    );
  };

  const getHeader = () => {
    return (
      <Stack direction="row" px={2} justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            disabled={isTruthy(managingStep)}
            onClick={() => props.setActiveStep(0)}
          >
            <SvgIcon>
              <KeyboardBackspaceIcon fontSize="small" />
            </SvgIcon>
          </IconButton>
          {editName ? (
            <TextField
              size="small"
              variant="outlined"
              placeholder="Campaign Name"
              value={formFields.name.value}
              onChange={(event: any) => {
                setFormFields({
                  ...formFields,
                  name: {
                    value: event.target.value,
                    error: "",
                  },
                });
              }}
              required
              error={isTruthy(formFields.name.error)}
              inputProps={{ minLength: 1, maxLength: 90 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {formFields.name?.value?.length} / 90
                    </span>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <Typography
              variant="h4"
              noWrap
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              maxWidth="250px"
              display="block"
            >
              {submitCampaign.campaign.name}
            </Typography>
          )}
          {editName ? (
            <>
              <IconButton
                onClick={saveCampaignName}
                disabled={isTruthy(managingStep)}
              >
                <SvgIcon>
                  <CheckIcon fontSize="small" />
                </SvgIcon>
              </IconButton>
              <IconButton
                onClick={() => setEditName(false)}
                disabled={isTruthy(managingStep)}
              >
                <SvgIcon>
                  <CloseIcon fontSize="small" />
                </SvgIcon>
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={() => {
                setFormFields({
                  ...formFields,
                  name: {
                    error: "",
                    value: submitCampaign.campaign.name,
                  },
                });
                setEditName(true);
              }}
              disabled={isTruthy(managingStep)}
            >
              <SvgIcon>
                <ModeEditIcon fontSize="small" />
              </SvgIcon>
            </IconButton>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            onClick={() => setIsTestModalOpen(true)}
            disabled={isTruthy(managingStep) || disableSendTestMail()}
          >
            Send test email
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const clonedChildCampaigns = cloneDeep(props.childCampaigns);
              const findChild = clonedChildCampaigns.findIndex(
                (child) => child.campaign.id === submitCampaign.campaign.id
              );
              clonedChildCampaigns[findChild] = submitCampaign;
              props.setChildCampaigns(clonedChildCampaigns);
              props.setActiveStep(0);
            }}
            disabled={isTruthy(managingStep) && isTimeInPast}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getBody = () => {
    return (
      <Card>
        <Stack spacing={4} py={4} divider={<Divider />}>
          <SubjectSection />
          <DesignSection
            setIsDesignEmailModalOpen={setIsDesignEmailModalOpen}
            design={design}
          />
          <ScheduleSection
            isTimeInPast={isTimeInPast}
            setIsTimeInPast={setIsTimeInPast}
          />
          <AttachmentSection />
        </Stack>
      </Card>
    );
  };

  const getTestModal = () => {
    return (
      <TestModal
        handleDialogClose={() => setIsTestModalOpen(false)}
        isDialogOpen={isTestModalOpen}
      />
    );
  };

  const getDesignEmailModal = () => {
    return (
      <DesignEmail
        handleDialogClose={() => setIsDesignEmailModalOpen(false)}
        isDialogOpen={isDesignEmailModalOpen}
        doneEditing={(content: any) => {
          setDesign(content);
          setIsDesignEmailModalOpen(false);
          setSubmitCampaign({
            ...submitCampaign,
            campaign: {
              ...submitCampaign.campaign,
              content: content.content,
              contentJson: content.contentJson,
              contentPlain: content.contentPlain,
            },
          });
        }}
        design={design}
        setDesign={setDesign}
      />
    );
  };

  const getPage = () => {
    return (
      <Stack spacing={4}>
        {getHeader()}
        {getBody()}
      </Stack>
    );
  };

  return (
    <>
      {getPage()}
      {getDesignEmailModal()}
      {getTestModal()}
    </>
  );
};

export default Step2;
